import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Combobox } from '../ui/combobox';
import type { PipelineNode } from '../../services/pipelineNode.service';

interface ExportConfigTabProps {
  workingCopy: PipelineNode | null;
  onChange: (updates: Partial<PipelineNode>) => void;
}

export function ExportConfigTab({ workingCopy, onChange }: ExportConfigTabProps) {
  if (!workingCopy) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Node Export</CardTitle>
        <p className="text-sm text-muted-foreground">
          Export this node's data to the SFTP exports folder after each scheduled build. Only new or updated records (based on the replication key) will be exported.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-2">
          <Switch
            id="export-enabled"
            checked={workingCopy.export_config?.enabled || false}
            onCheckedChange={(checked) => {
              onChange({
                export_config: {
                  ...workingCopy.export_config,
                  enabled: checked,
                  replication_key_field_id: checked ? workingCopy.export_config?.replication_key_field_id : undefined,
                },
              });
            }}
          />
          <Label htmlFor="export-enabled">Enable node export</Label>
        </div>
        {workingCopy.export_config?.enabled && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Replication Key Column</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Select the column used to track which records have already been exported. Only records where this column's value is greater than or equal to the last exported value will be included.
              </p>
              <Combobox
                options={(workingCopy.fields || [])
                  .filter(field => field.name)
                  .map(field => ({
                    value: field.id || field.name || '',
                    label: field.label || field.name || 'Unnamed Column',
                  }))}
                value={workingCopy.export_config?.replication_key_field_id || ''}
                onValueChange={(value) => {
                  onChange({
                    export_config: {
                      ...workingCopy.export_config,
                      enabled: true,
                      replication_key_field_id: value || undefined,
                    },
                  });
                }}
                placeholder="Select replication key column..."
                emptyText="No columns available."
              />
            </div>
            {workingCopy.export_config?.cursor && (
              <div className="text-sm text-muted-foreground">
                <span className="font-medium">Last exported cursor:</span> {workingCopy.export_config.cursor}
              </div>
            )}
            <div className="text-sm text-muted-foreground">
              <span className="font-medium">Export path:</span> tenant/{'<slug>'}/exports/{workingCopy.name || '<node_name>'}/
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
